/**
 * Centralised toast copy for the P2P chat feature (Bulgarian UI).
 *
 * Single source of truth for the cap, pin-cap, duplicate-chat and dead-invite
 * toasts so the wording cannot drift across the (previously duplicated) call
 * sites. Numeric limits are sourced from the store constants so the copy stays
 * correct if a cap changes.
 *
 * @module features/p2p-chat/constants/chatToasts
 */

import { MAX_PINNED, MAX_SESSIONS } from '../stores/p2pChatStore';

/** Shape shared by all toast copy helpers/constants. */
export interface ChatToastCopy {
  readonly title: string;
  readonly description: string;
}

/**
 * Primary message shown when a chat has ended. Shared between the inline ended
 * panel ({@link ChatScreen}) and the dead-on-arrival invite toast so both paths
 * read identically (AC1.3).
 */
export const CHAT_ENDED_PRIMARY = 'Този чат е приключил';

/** Concurrent-chat cap reached. Limit sourced from `MAX_SESSIONS` (AC3.1/AC3.5). */
export const CHAT_CAP_TOAST: ChatToastCopy = {
  title: 'Достигнат лимит',
  description: `Имаш ${MAX_SESSIONS} активни чата. Затвори някой, за да продължиш.`,
};

/** Pinned-chat cap reached. Distinct title from the chat cap (AC3.3). */
export const PIN_CAP_TOAST: ChatToastCopy = {
  title: 'Лимит за закачени чатове',
  description: `Можеш да закачиш най-много ${MAX_PINNED} чата.`,
};

/** Dead-on-arrival invite link: the room no longer exists (Item 1 / AC1.2). */
export const DEAD_INVITE_TOAST: ChatToastCopy = {
  title: CHAT_ENDED_PRIMARY,
  description: 'Стаята вече не съществува.',
};

/**
 * Duplicate-chat toast. When the peer's name is known (roster invite) the copy
 * names them; otherwise (manually pasted room code) it falls back to a generic
 * equivalent so the two entry paths stay consistent (AC3.2).
 */
export function duplicateChatToast(name?: string | null): ChatToastCopy {
  return {
    title: 'Чатът вече е отворен',
    description: name
      ? `Вече имаш активен чат с ${name}.`
      : 'Превключих към съществуващия разговор.',
  };
}
